import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import InjuryModal from "./InjuryModal";
import OpponentTradeModal from "./OpponentTradeModal";
import "./GameResults.css";
import "./variables.css";
import API_URL from "./config";
import { getTeamTheme } from "./teamTheme";

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const shortenName = (fullName) => {
  if (!fullName) return "—";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(" ")}`;
};

const POS_CLASSES = ["PG", "SG", "SF", "PF", "C"];

const PosBadge = ({ pos }) => {
  const cls = POS_CLASSES.includes(pos) ? pos : "default";
  return <span className={`pos-badge ${cls}`}>{pos || "?"}</span>;
};

const STAT_COLS = [
  { label: "MIN", key: "minutes" },
  { label: "PTS", key: "points", key2: true },
  { label: "REB", key: "rebounds", key2: true },
  { label: "AST", key: "assists", key2: true },
  { label: "STL", key: "steals" },
  { label: "BLK", key: "blocks" },
  { label: "FG",  shooting: ["fieldGoalsMade", "fieldGoalsAttempted"] },
  { label: "3PT", shooting: ["threePointersMade", "threePointersAttempted"] },
  { label: "FT",  shooting: ["freeThrowsMade", "freeThrowsAttempted"] },
];

const PlayerRow = ({ player, isBench, rowIndex, bestPlayers }) => {
  // Check if this player is among the best in any of the key stats
  let bestStats = [];
  if (bestPlayers) {
    if (bestPlayers.pts?.some(p => p.playerName === player.playerName)) bestStats.push("pts");
    if (bestPlayers.reb?.some(p => p.playerName === player.playerName)) bestStats.push("reb");
    if (bestPlayers.ast?.some(p => p.playerName === player.playerName)) bestStats.push("ast");
  }

  return (
    <div
      className={`stat-row${isBench ? " bench-row" : ""} row-animate`}
      style={{ animationDelay: `${1.8 + rowIndex * 0.05}s` }}
    >
      <div className="player-cell">
        <PosBadge pos={player.position} />
        <span className="player-name-text">
          {shortenName(player.playerName)}
          {bestStats.map((stat) => (
            <span key={stat} className={`best-stat-star ${stat}`}>★</span>
          ))}
        </span>
      </div>
      {STAT_COLS.map((col) => {
        let statClass = "";
        if (col.label === "PTS") statClass = "stat-pts";
        else if (col.label === "REB") statClass = "stat-reb";
        else if (col.label === "AST") statClass = "stat-ast";

        if (col.shooting) {
          const [made, att] = col.shooting;
          return (
            <span key={col.label} className={`stat-value shooting ${statClass}`}>
              {player[made] ?? 0}/{player[att] ?? 0}
            </span>
          );
        }
        return (
          <span key={col.label} className={`stat-value${statClass ? ` ${statClass}` : ""}`}>
            {player[col.key] ?? 0}
          </span>
        );
      })}
    </div>
  );
};

const StatTable = ({ teamName, stats, theme }) => {
  const positionOrder = { "PG": 0, "SG": 1, "SF": 2, "PF": 3, "C": 4 };
  const sortByPosition = (players) => {
    return [...players].sort((a, b) => {
      const posA = positionOrder[a.position] ?? 999;
      const posB = positionOrder[b.position] ?? 999;
      return posA - posB;
    });
  };

  const hasStarterInfo = stats.some((s) => s.isStarter !== undefined && s.isStarter !== null);
  const starters = sortByPosition(hasStarterInfo ? stats.filter((s) => s.isStarter) : stats.slice(0, 5));
  const bench    = sortByPosition(hasStarterInfo ? stats.filter((s) => !s.isStarter) : stats.slice(5));

  // Find best players for each key stat (including ties)
  const maxPoints = Math.max(...stats.map(p => p.points ?? 0), 0);
  const maxRebounds = Math.max(...stats.map(p => p.rebounds ?? 0), 0);
  const maxAssists = Math.max(...stats.map(p => p.assists ?? 0), 0);
  
  const bestPlayers = {
    pts: stats.filter(p => (p.points ?? 0) === maxPoints),
    reb: stats.filter(p => (p.rebounds ?? 0) === maxRebounds),
    ast: stats.filter(p => (p.assists ?? 0) === maxAssists),
  };

  return (
    <div className="stats-box">
      <div className="stats-box-header" style={{ borderBottomColor: hexToRgba(theme.primary, 0.35) }}>
        <div
          className="stats-team-badge"
          style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
        >
          {teamName.charAt(0)}
        </div>
        <h2 className="stats-title">{teamName}</h2>
      </div>
      <div className="stats-header">
        <span className="header-player">Player</span>
        {STAT_COLS.map((col) => {
          let statClass = "";
          if (col.label === "PTS") statClass = "stat-pts";
          else if (col.label === "REB") statClass = "stat-reb";
          else if (col.label === "AST") statClass = "stat-ast";
          
          return (
            <span key={col.label} className={`header-stat${col.key2 ? " key-stat" : ""} ${statClass}`}>
              {col.label}
            </span>
          );
        })}
      </div>
      <div className="stats-list">
        {starters.length > 0 && (
          <>
            <div
              className="stat-section-label"
              style={{ "--section-label-color": hexToRgba(theme.secondary, 0.7) }}
            >
              Starters
            </div>
            {starters.map((p, i) => <PlayerRow key={i} player={p} isBench={false} rowIndex={i} bestPlayers={bestPlayers} />)}
          </>
        )}
        {bench.length > 0 && (
          <>
            <div className="stat-section-label">Bench</div>
            {bench.map((p, i) => <PlayerRow key={i} player={p} isBench={true} rowIndex={starters.length + i} bestPlayers={bestPlayers} />)}
          </>
        )}
        {stats.length === 0 && (
          <div className="empty-message">No player stats available.</div>
        )}
      </div>
    </div>
  );
};

const InjuryReport = ({ injuries, theme }) => {
  if (!injuries) return null;
  return (
    <div className="injury-report">
      <div className="injury-report-header">
        <span className="injury-report-title">🏥 Injury Report</span>
      </div>
      <div className="injury-list">
        {injuries.length === 0 ? (
          <div className="injury-none">No injuries this game.</div>
        ) : (
          injuries.map((inj, i) => (
            <div key={i} className="injury-card">
              <span className="injury-player">{inj.playerName}</span>
              <span className="injury-name">{inj.injury}</span>
              <span className="injury-out">Out {inj.gamesOut} game{inj.gamesOut !== 1 ? "s" : ""}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const CATEGORY_LABELS = {
  momentum_shift: "Momentum Shift",
  player_psychology: "Player Psychology",
  coaching: "Coaching Decision",
  external: "External Factor",
  risk_reward: "Risk vs Reward",
  injury_fatigue: "Injury & Fatigue",
};

const MOMENT_NAMES = {
  the_run: "The Run",
  hot_hand: "The Hot Hand",
  foul_trouble: "Foul Trouble",
  hostile_crowd: "Hostile Territory",
  home_surge: "Crowd Surge",
  pace_adjustment: "Tempo Decision",
  injury_scare: "Injury Scare",
  clutch_time: "Crunch Time",
  cold_shooting: "Cold Shooting",
  ref_ball: "Questionable Call",
  bench_spark: "Flat Start",
};

const MomentTimeline = ({ moments, quarterScores }) => {
  if (!moments || moments.length === 0) return null;

  return (
    <div className="moments-timeline">
      <div className="moments-timeline-header">
        <span className="moments-timeline-title">Key Moments</span>
        <span className="moments-timeline-subtitle">Your decisions shaped this game</span>
      </div>
      <div className="moments-timeline-track">
        {[1, 2, 3, 4].map((q) => {
          const qMoments = moments.filter((m) => m.quarter === q);
          const hasQScore = quarterScores?.home?.[q - 1] !== undefined;

          return (
            <div key={q} className="timeline-quarter">
              <div className="timeline-quarter-marker">
                <div className="timeline-quarter-dot" />
                <span className="timeline-quarter-label">Q{q}</span>
              </div>

              {qMoments.length > 0 ? (
                qMoments.map((m, i) => (
                  <div key={i} className="timeline-moment-card">
                    <div className="timeline-moment-name">
                      {MOMENT_NAMES[m.id] || m.id}
                    </div>
                    {m.narrative && (
                      <div className="timeline-moment-narrative">
                        {m.narrative}
                      </div>
                    )}
                  </div>
                ))
              ) : hasQScore ? (
                <div className="timeline-no-moment">No moment this quarter</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function GameResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const homeTeam = state.homeTeam || "Home Team";
  const awayTeam = state.awayTeam || "Away Team";
  const homeScore = state.homeScore || 0;
  const awayScore = state.awayScore || 0;
  const homeStarterIds = state.homeStarterIds || [];
  const awayStarterIds = state.awayStarterIds || [];
  const userTeamId = state.userTeamId;
  
  const homeInjuries = state.homeInjuries || [];
  const awayInjuries = state.awayInjuries || [];
  const tradeProposal = state.tradeProposal || null;
  const moments = state.moments || [];
  const quarterScores = state.quarterScores || null;

  // Prefer engine-reported isStarter (survives game resume). Fall back to the
  // starter-id lists from navigation state only if the engine didn't mark it.
  const homeTeamStats = (state.homeTeamStats || []).map(stat => ({
    ...stat,
    isStarter: stat.isStarter !== undefined
      ? stat.isStarter
      : homeStarterIds.includes(stat.playerId),
  }));
  const awayTeamStats = (state.awayTeamStats || []).map(stat => ({
    ...stat,
    isStarter: stat.isStarter !== undefined
      ? stat.isStarter
      : awayStarterIds.includes(stat.playerId),
  }));

  const homeTheme = getTeamTheme(homeTeam);
  const awayTheme = getTeamTheme(awayTeam);
  const homeWon = homeScore > awayScore;
  const awayWon = awayScore > homeScore;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [homeInjuryData, setHomeInjuryData] = useState([]);
  const [awayInjuryData, setAwayInjuryData] = useState([]);
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);

  // Animated scores
  const [displayHomeScore, setDisplayHomeScore] = useState(0);
  const [displayAwayScore, setDisplayAwayScore] = useState(0);
  const [scoresRevealed, setScoresRevealed] = useState(false);
  const intervalRef = useRef(null);
  const hasSavedRef = useRef(false);  // Track if we've already auto-saved

  useEffect(() => {
    if (!homeScore && !awayScore) { setScoresRevealed(true); return; }
    const DURATION = 1800;
    const STEPS = 60;
    let current = 0;
    intervalRef.current = setInterval(() => {
      current++;
      const t = current / STEPS;
      // Ease out: starts fast, slows at the end
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayHomeScore(Math.min(Math.round(homeScore * eased), homeScore));
      setDisplayAwayScore(Math.min(Math.round(awayScore * eased), awayScore));
      if (current >= STEPS) {
        clearInterval(intervalRef.current);
        setDisplayHomeScore(homeScore);
        setDisplayAwayScore(awayScore);
        setScoresRevealed(true);
      }
    }, DURATION / STEPS);
    return () => clearInterval(intervalRef.current);
  }, [homeScore, awayScore]);

  // Auto-save results after scores finish animating
  useEffect(() => {
    if (scoresRevealed && !hasSavedRef.current) {
      hasSavedRef.current = true;
      // Small delay to ensure state is stable
      const timeoutId = setTimeout(() => {
        saveAndReturn();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [scoresRevealed]);

  // Show trade modal after winner is revealed (with 0.5s delay)
  useEffect(() => {
    if (scoresRevealed && tradeProposal) {
      const timeoutId = setTimeout(() => {
        setShowTradeModal(true);
      }, 700);
      return () => clearTimeout(timeoutId);
    }
  }, [scoresRevealed, tradeProposal]);

  // Show injury modal when injuries exist and setting allows it
  useEffect(() => {
    const injuryReportSetting = localStorage.getItem("injuryReportSetting") || "yourTeam";
    if (injuryReportSetting === "none") {
      setShowInjuryModal(false);
      return;
    }
    
    // Validate that injury data has actual content
    const hasValidHomeInjuries = (homeInjuryData || []).some(i => i && i.playerId);
    const hasValidAwayInjuries = (awayInjuryData || []).some(i => i && i.playerId);
    
    // Check if there are actually injuries to show based on setting
    let hasInjuriesToShow = false;
    if (injuryReportSetting === "yourTeam") {
      // Show only if user's team has injuries
      const userTeamId = state?.userTeamId;
      const isUserHome = userTeamId === state?.homeTeamId;
      hasInjuriesToShow = isUserHome ? hasValidHomeInjuries : hasValidAwayInjuries;
    } else if (injuryReportSetting === "bothTeams") {
      // Show if either team has injuries
      hasInjuriesToShow = hasValidHomeInjuries || hasValidAwayInjuries;
    }
    
    setShowInjuryModal(hasInjuriesToShow);
  }, [homeInjuryData, awayInjuryData, state?.userTeamId, state?.homeTeamId]);

  // Prevent navigation without saving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasSavedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const saveAndReturn = async () => {
    try {
      setSaving(true);
      setError("");

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;
      const activeSaveId = localStorage.getItem("activeSaveId");

      if (!userId || !activeSaveId) {
        setError("Please log in and select a save before saving results.");
        setSaving(false);
        return;
      }

      const homeTeamId = state.homeTeamId || state.homeTeam;
      const awayTeamId = state.awayTeamId || state.awayTeam;
      const playerStats = [
        ...(state.homeTeamStats || []).map(s => ({ ...s, teamId: homeTeamId })),
        ...(state.awayTeamStats || []).map(s => ({ ...s, teamId: awayTeamId })),
      ];

      const gameResult = {
        gameId: state.gameId || null,
        season: state.season || new Date().getFullYear(),
        homeTeam: homeTeamId,
        awayTeam: awayTeamId,
        homeScore: homeScore,
        awayScore: awayScore,
        playerStats,
      };

      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/games`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
        },
        body: JSON.stringify(gameResult),
      });

      if (!res.ok) {
        let data = null;
        try {
          data = await res.json();
        } catch (e) {
          data = null;
        }
        const formatErr = (d) => {
          if (!d) return "Failed to save result";
          if (typeof d === "string") return d;
          if (d.detail && typeof d.detail === "string") return d.detail;
          if (d.message && typeof d.message === "string") return d.message;
          try {
            return JSON.stringify(d);
          } catch (e) {
            return "Failed to save result";
          }
        };
        setError(formatErr(data));
        setSaving(false);
        return;
      }

      const saved = await res.json();
      const allInjuries = (saved?.injuries || saved?.result?.injuries || [])
        .filter(inj => inj && inj.playerId && inj.playerName); // Ensure valid injury objects
      
      const newHomeInjuries = allInjuries.filter(i =>
        (state.homeTeamStats || []).some(s => s.playerId === i.playerId)
      );
      const newAwayInjuries = allInjuries.filter(i =>
        (state.awayTeamStats || []).some(s => s.playerId === i.playerId)
      );
      
      setHomeInjuryData(newHomeInjuries);
      setAwayInjuryData(newAwayInjuries);
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const returnToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <>
      <Navbar />
      <div className="game-results-container">
        <div className="game-results-header">
          <h1 className="results-title">Final Score</h1>
          <p className="results-subtitle">
            {state.season ? `Season ${state.season}` : "Game Results"}
          </p>
        </div>

        {/* Matchup Hero */}
        <div className="matchup-hero">
          {/* Away Team */}
          <div
            className={`matchup-team away${awayWon && scoresRevealed ? " team-won" : ""}`}
            style={{ "--away-primary-alpha": hexToRgba(awayTheme.primary, 0.18) }}
          >
            <div
              className="team-badge"
              style={{ background: `linear-gradient(135deg, ${awayTheme.primary}, ${awayTheme.secondary})` }}
            >
              {awayTeam.charAt(0)}
            </div>
            <div className="matchup-location-label">AWAY</div>
            <div className="matchup-team-name">{awayTeam}</div>
            <div
              className={`matchup-score${awayWon && scoresRevealed ? " winner" : ""}${awayWon && scoresRevealed ? " score-glow" : ""}`}
            >
              {displayAwayScore}
            </div>
            {awayWon && scoresRevealed && (
              <span
                className="winner-badge winner-badge-pop"
                style={{
                  background: hexToRgba(awayTheme.primary, 0.25),
                  color: awayTheme.secondary,
                  border: `1px solid ${hexToRgba(awayTheme.secondary, 0.4)}`,
                }}
              >
                WINNER
              </span>
            )}
          </div>

          {/* Center column */}
          <div className="matchup-center">
            <span className="matchup-final-label">Final</span>
            <span className="matchup-vs">VS</span>
            {state.season && (
              <span className="matchup-meta">
                {`Season ${state.season}`}
              </span>
            )}
          </div>

          {/* Home Team */}
          <div
            className={`matchup-team home${homeWon && scoresRevealed ? " team-won" : ""}`}
            style={{ "--home-primary-alpha": hexToRgba(homeTheme.primary, 0.18) }}
          >
            <div
              className="team-badge"
              style={{ background: `linear-gradient(135deg, ${homeTheme.primary}, ${homeTheme.secondary})` }}
            >
              {homeTeam.charAt(0)}
            </div>
            <div className="matchup-location-label">HOME</div>
            <div className="matchup-team-name">{homeTeam}</div>
            <div
              className={`matchup-score${homeWon && scoresRevealed ? " winner" : ""}${homeWon && scoresRevealed ? " score-glow" : ""}`}
            >
              {displayHomeScore}
            </div>
            {homeWon && scoresRevealed && (
              <span
                className="winner-badge winner-badge-pop"
                style={{
                  background: hexToRgba(homeTheme.primary, 0.25),
                  color: homeTheme.secondary,
                  border: `1px solid ${hexToRgba(homeTheme.secondary, 0.4)}`,
                }}
              >
                WINNER
              </span>
            )}
          </div>
        </div>
        {/* Injury Modal */}
        {showInjuryModal && (
          <InjuryModal
            homeInjuries={homeInjuryData}
            awayInjuries={awayInjuryData}
            homeTeam={homeTeam}
            awayTeam={awayTeam}
            homeTeamId={state.homeTeamId}
            awayTeamId={state.awayTeamId}
            userTeamId={userTeamId}
            homeTheme={homeTheme}
            awayTheme={awayTheme}
            onClose={() => setShowInjuryModal(false)}
            reportSetting={localStorage.getItem("injuryReportSetting") || "yourTeam"}
          />
        )}

        {/* Opponent Trade Proposal Modal */}
        {showTradeModal && tradeProposal && (
          <OpponentTradeModal
            tradeProposal={tradeProposal}
            userTeamId={userTeamId}
            onClose={() => setShowTradeModal(false)}
            onTradeComplete={() => {
              // Refresh or update UI after trade if needed
            }}
          />
        )}

        {/* Moment Timeline (#5) */}
        {moments.length > 0 && (
          <MomentTimeline moments={moments} quarterScores={quarterScores} />
        )}

        {/* Stats */}
        <div className="stats-container">
          <StatTable teamName={awayTeam} stats={awayTeamStats} theme={awayTheme} />
          <StatTable teamName={homeTeam} stats={homeTeamStats} theme={homeTheme} />
        </div>

        {error && <div className="message">{error}</div>}

        <button
          className="results-button"
          onClick={returnToDashboard}
          disabled={saving}
        >
          {saving ? "Saving..." : "Return to Dashboard"}
        </button>
      </div>
    </>
  );
}

export default GameResults;
